/**
 * Social Media Contact Component for Community Engagement
 * 
 * A dual-purpose component that combines social media promotion with
 * contact information, encouraging both community engagement and
 * direct customer communication through multiple touchpoints.
 * 
 * Core Sections:
 * - Social Media: Instagram and Facebook integration with branded styling
 * - Contact Information: Direct communication channel with call-to-action
 * 
 * Social Media Features:
 * - Platform-specific styling (Facebook blue, Instagram gradient)
 * - External link security with noopener noreferrer
 * - Hover animations and visual feedback
 * - Brand-consistent icon presentation
 * - Community engagement messaging
 * 
 * Contact Integration:
 * - Clear call-to-action for customer inquiries
 * - Direct navigation to contact page
 * - Professional contact messaging
 * - Custom order and question handling
 * 
 * Content Management:
 * - Flexible content from Sanity CMS
 * - Portable Text integration for rich descriptions
 * - Fallback content for missing CMS data
 * - Configurable links and messaging
 * - Dynamic content rendering
 * 
 * Visual Design:
 * - Two-column responsive layout
 * - Brand font integration (Neuropol, Montserrat)
 * - Professional color scheme and spacing
 * - Consistent button styling and interactions
 * - Mobile-optimized layout stacking
 * 
 * User Experience:
 * - Clear differentiation between social and contact options
 * - Easy access to community engagement channels
 * - Direct path to customer support
 * - Engaging visual design with brand consistency
 * 
 * Flexibility:
 * - Configurable container styling
 * - Custom portable text rendering function
 * - Fallback content for all sections
 * - Flexible link configuration
 * 
 * Accessibility:
 * - Semantic HTML structure for content sections
 * - Descriptive link text and purposes
 * - High contrast button styling
 * - Keyboard navigation support
 * 
 * @param {string} containerClassName - Custom container styling class
 * @param {object} socialSection - Social media section content
 * @param {object} contactInfo - Contact information and messaging
 * @param {function} renderPortableText - Custom portable text renderer
 * 
 * @description Dual-purpose social media and contact component for community engagement
 */

import React from 'react';
import Link from 'next/link';
import { Instagram, Facebook } from 'lucide-react';
import { PortableText } from '@portabletext/react';

/**
 * Custom components for Portable Text rendering
 * Provides brand-consistent typography and formatting
 */
const portableTextComponents = {
  marks: {
    neuropol: ({children}) => <span className="font-neuropol">{children}</span>,
    montserrat: ({children}) => <span className="font-montserrat">{children}</span>,
    strong: ({children}) => <strong>{children}</strong>,
    em: ({children}) => <em>{children}</em>,
  },
  block: {
    normal: ({children}) => <p className="mb-4 last:mb-0">{children}</p>,
  },
};

const SocialMediaContact = ({ 
  containerClassName = "p-10", 
  socialSection, 
  contactInfo,
  renderPortableText
}) => {
  // If renderPortableText is not provided, use a default implementation
  const handlePortableText = renderPortableText || ((portableText, fallbackText) => {
    // Check if portableText exists, is an array, has content, and that content has text
    if (portableText && 
        Array.isArray(portableText) && 
        portableText.length > 0 && 
        portableText.some(block => 
          block.children && 
          block.children.length > 0 && 
          block.children.some(child => child.text && child.text.trim().length > 0)
        )
    ) {
      return <PortableText value={portableText} components={portableTextComponents} />;
    }
    // Return fallback text if any of the above conditions aren't met
    return <p>{fallbackText}</p>;
  });

  return (
    <div className={containerClassName}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Social Media Section */}
        <div>
          <h3 className="text-2xl font-bold mb-4 font-neuropol">{socialSection?.title || 'Follow Our Journey'}</h3>
          <div className="text-gray-700 mb-6 font-montserrat">
            {handlePortableText(
              socialSection?.descriptionData, 
              socialSection?.fallbackDescription || 'Stay updated with our latest designs, events, and behind-the-scenes moments.'
            )}
          </div>
          <div className="flex space-x-4">
            <a 
              href={socialSection?.facebookLink || 'https://facebook.com/woodalps'} 
              target="_blank" 
              rel="noopener noreferrer"
              className="p-3 bg-[#1877F2] rounded-full text-white hover:bg-[#1877F2]/90 transition-all"
            >
              <Facebook className="w-6 h-6" />
            </a>
            <a 
              href={socialSection?.instagramLink || 'https://www.instagram.com/woodalps_wooden_bikes/'} 
              target="_blank" 
              rel="noopener noreferrer"
              className="p-3 bg-gradient-to-br from-[#833AB4] via-[#FD1D1D] to-[#FCAF45] rounded-full text-white hover:opacity-90 transition-all"
            >
              <Instagram className="w-6 h-6" />
            </a>
          </div>
        </div>
        
        {/* Contact Information */}
        <div>
          <h3 className="text-2xl font-bold mb-4 font-neuropol">{contactInfo?.title || 'Let\'s Talk About Your Dream Bike'}</h3>
          <div className="text-gray-700 mb-6 font-montserrat">
            {handlePortableText(
              contactInfo?.descriptionData,
              contactInfo?.fallbackDescription || 'Whether you have questions about our products, custom orders, or just want to say hello, we\'d love to hear from you.'
            )}
          </div>
          <Link 
            href={contactInfo?.buttonLink || '/contact'}
            className="inline-flex items-center px-6 py-3 bg-accent-color text-white rounded-md hover:bg-accent-color/90 transition-all duration-300 font-montserrat"
          >
            {contactInfo?.buttonText || 'Contact Us'}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SocialMediaContact; 